/**
 * CORE-E — what a category score actually rests on.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS REPLACES A SENTENCE THAT IS NO LONGER TRUE
 * ═══════════════════════════════════════════════════════════════════════════
 * The heatmap's category detail told every reader:
 *
 *   "CORE-E stores no provenance field, so neither this screen nor anyone
 *    reading it can tell a manual baseline from a synced shop value from
 *    derived evidence."
 *
 * That was honest when it was written and is now false. `20261122120000` gave
 * CORE-E an authority — `technician_category_authority` — with a baseline, its
 * class, an evidence count, a confidence and a defect adjustment, and a durable
 * evidence record in `technician_proficiency_evidence` that carries reversal.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * IT EXPLAINS THE SCHEDULING NUMBER, NOT A MIRROR OF IT
 * ═══════════════════════════════════════════════════════════════════════════
 * The score shown here is the authority's own `score` — the value assignment
 * and scheduling consume. Explaining the portable Passport snapshot instead
 * would describe a number nobody is deciding with, which is the exact defect
 * this campaign keeps finding.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IT DELIBERATELY DOES NOT SHOW
 * ═══════════════════════════════════════════════════════════════════════════
 * Not the blending. A person is owed what their score rests on — a prior, how
 * much work stands behind it, which work moved it and which was reversed — not
 * the weighting curve that combines them. The one structural fact stated is
 * whether demonstrated work now outweighs the opening estimate, because that
 * answers "is this still somebody's guess about me?", which is a question about
 * them rather than about the algorithm.
 *
 * And not training readiness. Readiness is what somebody has been TAUGHT;
 * proficiency is what they have DEMONSTRATED. CORE-G's sibling rule applies
 * here too — they are not synonyms and must not be substituted — so this panel
 * says nothing about readiness at all rather than risk it reading as evidence.
 */

import {
  evidenceHasSupersededPrior,
  type CategoryAuthority,
  type BaselineClass,
} from '@/hooks/passport/useCategoryAuthority';

/** What is actually known about where an opening prior came from. */
const BASELINE_CLASS_LABEL: Record<BaselineClass, string> = {
  MANAGER_ASSERTED: 'Opening estimate set by a manager',
  PASSPORT_CARRIED: 'Carried in from your own Passport',
  LEGACY_UNCLASSIFIED: 'Opening value with no recorded source',
};

const BASELINE_CLASS_DETAIL: Record<BaselineClass, string> = {
  MANAGER_ASSERTED:
    'Somebody at this shop stated this as a starting point. It is an estimate, not something measured.',
  PASSPORT_CARRIED:
    'This came with you from your own Passport when you joined, rather than being stated by this shop.',
  LEGACY_UNCLASSIFIED:
    'This value was already here before the system recorded where opening values came from, so nobody can say who set it. It is not being guessed at.',
};

interface CategoryAuthorityExplanationProps {
  categoryKey: string;
  data: CategoryAuthority | undefined;
  /** True when the Passport is not attached to a shop roster row. */
  hasNoShopContext?: boolean;
}

export function CategoryAuthorityExplanation({
  categoryKey,
  data,
  hasNoShopContext,
}: CategoryAuthorityExplanationProps) {
  if (hasNoShopContext) {
    return (
      <p
        className="text-[11px] text-muted-foreground italic"
        data-testid="core-e-no-shop-context"
      >
        You are not currently attached to a shop roster, so there is no
        scheduling score to explain. What is shown above is your portable
        record.
      </p>
    );
  }

  const authority = data?.authority ?? null;

  if (!authority) {
    return (
      <p
        className="text-[11px] text-muted-foreground italic"
        data-testid="core-e-no-authority"
      >
        No scheduling score has been established for this category yet.
      </p>
    );
  }

  const evidence = data?.evidence ?? [];
  const live = evidence.filter((e) => !e.reversedAt);
  const reversed = evidence.filter((e) => e.reversedAt);
  const clean = live.filter((e) => e.kind === 'CLEAN_MATURED');
  // CORE-AM: "MUST NOT apply a negative adjustment on a comeback whose
  // attribution has not been established." Unattributed defects are recorded
  // and are NOT part of what moved the score, so they are not shown as though
  // they were.
  const attributedDefects = live.filter((e) => e.kind === 'ATTRIBUTED_DEFECT');
  // Stage A: the crossing point belongs to this row's prior, not to a global
  // constant. Identical while every prior is worth 8.
  const superseded = evidenceHasSupersededPrior(
    authority.evidenceCount,
    authority.baselinePriorStrength,
  );

  return (
    <div className="space-y-2 text-[11px]" data-testid="core-e-explanation" data-category={categoryKey}>
      {/* The number decisions are made with. */}
      <p data-testid="core-e-authoritative-score">
        <span className="text-muted-foreground">Scheduling uses </span>
        <span className="font-semibold">
          {authority.score === null ? 'no score yet' : Math.round(authority.score)}
        </span>
        {authority.score !== null && (
          <span className="text-muted-foreground"> for this category.</span>
        )}
      </p>

      {/* The prior, and what is actually known about it. */}
      {authority.baselineScore !== null && authority.baselineClass && (
        <p data-testid="core-e-baseline" data-baseline-class={authority.baselineClass}>
          <span className="text-muted-foreground">Started from </span>
          <span className="font-medium">{Math.round(authority.baselineScore)}</span>
          <span className="text-muted-foreground">
            {' — '}
            {BASELINE_CLASS_LABEL[authority.baselineClass]}.{' '}
            {BASELINE_CLASS_DETAIL[authority.baselineClass]}
          </span>
        </p>
      )}
      {authority.baselineScore === null && (
        <p className="text-muted-foreground" data-testid="core-e-no-baseline">
          There was no opening estimate for this category — the score rests on
          demonstrated work alone.
        </p>
      )}

      {/* How much work stands behind it. */}
      <p data-testid="core-e-sample">
        <span className="text-muted-foreground">Based on </span>
        <span className="font-medium">
          {authority.evidenceCount} {authority.evidenceCount === 1 ? 'piece' : 'pieces'} of evidence
        </span>
        <span className="text-muted-foreground">.</span>
      </p>

      {/* How well it is supported. Its own line, not a suffix of the count:
          since CORE-E Stage E confidence is NOT a function of how many pieces
          of evidence exist — a hundred routine jobs do not make OptiBay more
          sure of a 96 — and reading "N pieces · confidence X%" as one clause
          said that it was. */}
      <p data-testid="core-e-confidence">
        <span className="text-muted-foreground">Confidence: </span>
        <span className="font-medium">{Math.round(authority.confidence * 100)}%</span>
      </p>

      {/* Whether it is still somebody's guess. */}
      <p
        data-testid="core-e-supersession"
        data-superseded={superseded ? 'true' : 'false'}
        className={superseded ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-300'}
      >
        {superseded
          ? 'Your demonstrated work now outweighs the opening estimate.'
          : 'The opening estimate still carries more weight than demonstrated work so far.'}
      </p>

      {/* What actually moved it. */}
      {clean.length > 0 && (
        <p data-testid="core-e-positive-evidence">
          <span className="text-muted-foreground">
            {clean.length} completed {clean.length === 1 ? 'job' : 'jobs'} held without a comeback
            and {clean.length === 1 ? 'counts' : 'count'} toward this score.
          </span>
        </p>
      )}
      {attributedDefects.length > 0 && (
        <p data-testid="core-e-defects">
          <span className="text-muted-foreground">
            {attributedDefects.length} attributed{' '}
            {attributedDefects.length === 1 ? 'defect' : 'defects'} lowered it
            {authority.defectAdjustment < 0
              ? ` by ${Math.abs(Math.round(authority.defectAdjustment))} in total.`
              : '.'}
          </span>
        </p>
      )}
      {clean.length === 0 && attributedDefects.length === 0 && (
        <p className="text-muted-foreground" data-testid="core-e-no-evidence">
          No completed work has counted toward this category yet.
        </p>
      )}

      {/* Reversal is a fact about the person's record and they are owed it. */}
      {reversed.length > 0 && (
        <p data-testid="core-e-reversed">
          <span className="text-muted-foreground">
            {reversed.length} earlier{' '}
            {reversed.length === 1 ? 'piece' : 'pieces'} of evidence{' '}
            {reversed.length === 1 ? 'was' : 'were'} reversed and no longer{' '}
            {reversed.length === 1 ? 'counts' : 'count'}.
          </span>
        </p>
      )}
    </div>
  );
}
